import { createAction, createReducer } from '@reduxjs/toolkit';
import {
	differenceBy,
	find,
	get,
	intersectionWith,
	isUndefined,
	map,
	without,
} from 'lodash-es';
import { DEFAULT_SIP_FILTER_METADATA } from '../../../constants/equity/orderBook/orderBookLanding';

const MODULE_PREFIX = 'ORDER BOOK';

const Actions = {
	getAllOrders: `${MODULE_PREFIX} getAllOrders`,
	getAllOrdersThrottled: `${MODULE_PREFIX} getAllOrdersThrottled`,
	setAllOrders: `${MODULE_PREFIX} setAllOrders`,
	getSipOrders: `${MODULE_PREFIX} getSipOrders`,
	setSipOrders: `${MODULE_PREFIX} setSipOrders`,
	modifySipData: `${MODULE_PREFIX} modifySipData`,
	setIsModifySipData: `${MODULE_PREFIX} setIsModifySipData`,
	setSelectedOpenOrders: `${MODULE_PREFIX} setSelectedOpenOrders`,
	selectedAllOpenOrders: `${MODULE_PREFIX} selectedAllOpenOrders`,
	updateSelectedOpenOrders: `${MODULE_PREFIX} updateSelectedOpenOrders`,
	cancelSelectedOpenOrders: `${MODULE_PREFIX} cancelSelectedOpenOrders`,
	setSipOrderTransactions: `${MODULE_PREFIX} setSipOrderTransactions`,
	getSipOrderTransactions: `${MODULE_PREFIX} getSipOrderTransactions`,
	setModifiedSipData: `${MODULE_PREFIX} setModifiedSipData`,
	getSelectOrder: `${MODULE_PREFIX} getSelectOrder`,
	setSelectOrderDetails: `${MODULE_PREFIX} setSelectOrderDetails`,
	cancelOrder: `${MODULE_PREFIX} cancelOrder`,
	fetchTradeDetails: `${MODULE_PREFIX} fetchTradeDetails`,
	setTradeDetails: `${MODULE_PREFIX} setTradeDetails`,
	fetchSingleOrderDetails: `${MODULE_PREFIX} fetchSingleOrderDetails`,
	setDetailSheetStatus: `${MODULE_PREFIX} setDetailSheetStatus`,
	setIsEventTriggeredWithSheet: `${MODULE_PREFIX} setIsEventTriggeredWithSheet`,
	setOrderTabLoader: `${MODULE_PREFIX} setOrderTabLoader`,
	setOrdersLoadingState: `${MODULE_PREFIX} setOrdersLoadingState`,
};

// actions

export const getAllOrders = createAction(Actions.getAllOrders);
export const getAllOrdersThrottled = createAction(Actions.getAllOrdersThrottled);
export const setAllOrders = createAction(Actions.setAllOrders);
export const setOrderTabLoader = createAction(Actions.setOrderTabLoader);
export const getSipOrders = createAction(Actions.getSipOrders);
export const setSipOrders = createAction(Actions.setSipOrders);
export const modifySipData = createAction(Actions.modifySipData);
export const setIsModifySipData = createAction(Actions.setIsModifySipData);
export const setModifiedSipData = createAction(Actions.setModifiedSipData);
export const setSelectedOpenOrders = createAction(
	Actions.setSelectedOpenOrders,
);
export const selectedAllOpenOrders = createAction(
	Actions.selectedAllOpenOrders,
);
export const updateSelectedOpenOrders = createAction(
	Actions.updateSelectedOpenOrders,
);
export const cancelSelectedOpenOrders = createAction(
	Actions.cancelSelectedOpenOrders,
);
export const setSipOrderTransactions = createAction(
	Actions.setSipOrderTransactions,
);
export const getSipOrderTransactions = createAction(
	Actions.getSipOrderTransactions,
);
export const setSelectOrderDetails = createAction(
	Actions.setSelectOrderDetails,
);
export const getSelectOrder = createAction(Actions.getSelectOrder);
export const cancelOrder = createAction(Actions.cancelOrder);
export const fetchTradeDetails = createAction(Actions.fetchTradeDetails);
export const setTradeDetails = createAction(Actions.setTradeDetails);
export const fetchSingleOrderDetails = createAction(
	Actions.fetchSingleOrderDetails,
);
export const setDetailSheetStatus = createAction(Actions.setDetailSheetStatus);
export const setIsEventTriggeredWithSheet = createAction(
	Actions.setIsEventTriggeredWithSheet,
);
export const setOrdersLoadingState = createAction(Actions.setOrdersLoadingState);

// selectors

export const getTradeDetails = (state) => {
	const data = get(state, 'orderBook.selectedTradeDetails', {});
	return data;
};

export const getSelectOrderDetails = (state) => {
	const data = get(state, 'orderBook.selectedOrderDetails', []);
	return data;
};

export const getExecutedOrdesData = (state) => {
	const excutedData = get(state, 'orderBook.executedOrders.data', []);
	return excutedData;
};

export const getExecutedOrdersRemovedItems = (state) => {
	return get(state, 'orderBook.removedItems', []);
};

export const getExecutedOrdesState = (state) => {
	const isLoading = get(state, 'orderBook.executedOrders.isLoading', false);
	const isEmptyList = get(state, 'orderBook.executedOrders.isEmpty', false);
	const isError = get(state, 'orderBook.executedOrders.isError', false);
	return { isLoading, isEmptyList, isError };
};

export const getOpenOrdersState = (state) => {
	const isLoading = get(state, 'orderBook.openOrders.isLoading', false);
	const isEmptyList = get(state, 'orderBook.openOrders.isEmpty', false);
	const isError = get(state, 'orderBook.openOrders.isError', false);
	return { isLoading, isEmptyList, isError };
};

export const getOpenOrdersData = (state) => {
	const openData = get(state, 'orderBook.openOrders.data', []);
	return openData;
};

export const getExecutedOrderFilterOptions = (state) => {
	const applicapleFilters = get(
		state,
		'orderBook.executedOrders.metadata',
		[],
	);
	return applicapleFilters;
};

export const getOpenOrderFilterOptions = (state) => {
	const applicapleFilters = get(state, 'orderBook.openOrders.metadata', []);
	return applicapleFilters;
};

export const getSelectedOpenOrders = (state) => {
	const selectedOpenOrders = get(state, 'orderBook.selectedOpenOrders', []);
	return selectedOpenOrders;
};

export const getSipOrdersData = (state) => {
	const allSipOrdersData = get(state, 'orderBook.sipOrders.data', []);
	return allSipOrdersData;
};

export const getSipOrdersFilterOptions = (state) => {
	const filtersFromMetadata = get(state, 'orderBook.sipOrders.metadata', []);
	return filtersFromMetadata;
};

export const getSipOrderTransactionsData = (state) => {
	const sipOrderTransactions = get(
		state,
		'orderBook.sipOrdersTransactions',
		DEFAULT_ARRAY_STATE,
	);
	return sipOrderTransactions;
};

export const getIsModifySipDataFlag = (state) => {
	const isModifySip = get(
		state,
		'orderBook.sipOrders.isModifySipData',
		false,
	);
	return isModifySip;
};

export const getSipOrderLoadingOrEmptyStates = (state) => {
	const isLoading = get(state, 'orderBook.sipOrders.isLoading', false);
	const isEmptyList = get(state, 'orderBook.sipOrders.isEmpty', false);
	const isError = get(state, 'orderBook.sipOrders.isError', false);
	return { isLoading, isEmptyList, isError };
};

export const getDetailSheetStatus = (state) => {
	const data = get(state, 'orderBook.isDetailSheetOpen', false);
	return data;
};

export const getIsEventTriggeredWithSheet = (state) => {
	const data = get(state, 'orderBook.isEventTriggeredWithSheet', false);
	return data;
};

export const getLoadingOrEmptyStateForSipModify = (state) => {
	const data = get(state, 'orderBook.sipModifyObject', false);
	return data;
};
export const getOrderTabLoader = (state) => {
	return get(state, 'orderBook.isOrderTabLoader', false);
};
// reducers

export const DEFAULT_ARRAY_STATE = {
	isLoading: false,
	data: [],
};

export const DEFAULT_OBJECT_STATE = {
	isLoading: false,
	data: {},
	isError: false,
};

const DEFAULT_META_DATA = {
	transaction_type: [],
	friendly_order_type: [],
	friendly_product_type: [],
	friendly_status: [],
};

const DEFAULT_ORDERS_STATE = {
	selectedOrderDetails: {},
	selectedTradeDetails: {
		isLoading: false,
		data: {},
	},
	openOrders: {
		isLoading: true,
		data: [],
		removedItems: [],
		isEmptyList: false,
		metadata: DEFAULT_META_DATA,
		isError: false,
	},
	executedOrders: {
		isLoading: true,
		data: [],
		isEmptyList: false,
		isError: false,
		metadata: DEFAULT_META_DATA,
	},
	sipOrders: {
		isLoading: true,
		data: [],
		isEmptyList: false,
		metadata: {
			friendly_status: DEFAULT_SIP_FILTER_METADATA,
		},
		isModifySipData: false,
		isError: false,
	},
	selectedOpenOrders: [],
	sipOrdersTransactions: { isLoading: false, data: [], isError: false },
	isEventTriggeredWithSheet: false,
	isDetailSheetOpen: false,
	sipModifyObject: DEFAULT_OBJECT_STATE,
	isOrderTabLoader: true,
};

const initialState = DEFAULT_ORDERS_STATE;

export default createReducer(initialState, (builder) => {
	builder.addCase(setAllOrders, (state, action) => {
		const { data = {} } = action.payload;
		const { open = {}, executed = {} } = data;
		const openOrdersData =
			open?.data || DEFAULT_ORDERS_STATE.openOrders.data;
		const openOrdersMetadata =
			open?.metadata || DEFAULT_ORDERS_STATE.openOrders.metadata;
		const executedOrdersData =
			executed?.data || DEFAULT_ORDERS_STATE.executedOrders.data;
		const executedOrdersMetadata =
			executed?.metadata || DEFAULT_ORDERS_STATE.executedOrders.metadata;

		state.executedOrders.data = executedOrdersData;
		state.executedOrders.metadata = executedOrdersMetadata;
		state.openOrders.data = openOrdersData;
		state.openOrders.metadata = openOrdersMetadata;

		// NOTE: Below code manages the selected items on the 'Cancel Open Orders' screen in response to a trade socket event. It updates the selected orders list when the event is received
 		state.openOrders.removedItems = differenceBy(
			state.openOrders.data,
			openOrdersData,
			'order_id',
		);

		if (state.selectedOpenOrders.length > 0) {
			state.selectedOpenOrders = intersectionWith(
				state.selectedOpenOrders,
				openOrdersData,
				(lsh, rsh) => lsh.order_id === rsh.order_id,
			);
		}
	});
	builder.addCase(setOrdersLoadingState, (state, action) => {
		const {
			isLoading = false,
			isError = false,
		} = action.payload;

		state.executedOrders.isLoading = isLoading;
		state.openOrders.isLoading = isLoading;
		state.openOrders.isError = isError;
		state.executedOrders.isError = isError;
	});
	builder.addCase(setSipOrders, (state, action) => {
		const {
			data = {},
			isLoading = false,
			isError = false,
		} = action.payload;
		const orderList = data?.data;
		const metadata = data?.metadata;
		state.sipOrders = { isLoading, data: orderList, metadata, isError };
	});

	builder.addCase(selectedAllOpenOrders, (state) => {
		const openOrders = get(state, 'openOrders.data', []);
		state.selectedOpenOrders = map(openOrders, (item) => {
			return {
				order_id: item.order_id,
				exchange: item.exchange,
				display_name: item.display_name,
			};
		});
	});

	builder.addCase(setSelectedOpenOrders, (state, action) => {
		state.selectedOpenOrders = action.payload;
	});

	builder.addCase(updateSelectedOpenOrders, (state, action) => {
		let list = [...state.selectedOpenOrders];
		const { order_id } = action.payload;
		const found = find(list, (item) => item.order_id === order_id);
		if (!isUndefined(found)) {
			list = without(list, found);
		} else {
			list.push(action.payload);
		}
		state.selectedOpenOrders = list;
	});
	builder.addCase(setSipOrderTransactions, (state, action) => {
		const sipOrdersData = state.sipOrdersTransactions;
		state.sipOrdersTransactions = { ...sipOrdersData, ...action.payload };
	});
	builder.addCase(setModifiedSipData, (state, action) => {
		const modifiedSipData = state.sipModifyObject;
		state.sipModifyObject = { ...modifiedSipData, ...action.payload };
	});
	builder.addCase(setSelectOrderDetails, (state, action) => {
		const data = action.payload;
		state.selectedOrderDetails = data;
	});
	builder.addCase(setTradeDetails, (state, action) => {
		const data = action.payload;
		state.selectedTradeDetails = { ...state.selectedTradeDetails, ...data };
	});
	builder.addCase(setIsModifySipData, (state, action) => {
		const { isModifySipData = false } = action.payload;
		state.sipOrders = { ...state.sipOrders, isModifySipData };
	});
	builder.addCase(setDetailSheetStatus, (state, action) => {
		const isOpen = action.payload;
		state.isDetailSheetOpen = isOpen;
	});
	builder.addCase(setIsEventTriggeredWithSheet, (state, action) => {
		const isTriggered = action.payload;
		state.isEventTriggeredWithSheet = isTriggered;
	});
	builder.addCase(setOrderTabLoader, (state, action) => {
		const isLoading = action.payload;
		state.isOrderTabLoader = isLoading;
	});
});
